import type { GridComparatorFn, GridSortDirection } from '@mui/x-data-grid-pro'

export type SortValueType = 'number' | 'text' | 'date'

const toComparable = (value: Maybe<string | number>, type: SortValueType): number | string | null => {
  if (value === null || value === undefined || value === '') return null
  if (type === 'text') return String(value).toLowerCase()
  if (type === 'date') {
    const time = new Date(value).getTime()
    return Number.isNaN(time) ? null : time
  }
  const num = Number(value)
  return Number.isNaN(num) ? null : num
}

// Comparator for a DataGrid column whose raw cell value needs normalising before
// comparison (pence strings → numbers, ISO dates → timestamps, text → lowercase).
// Missing or blank values always sort last, regardless of direction: the grid
// negates comparator results for desc, so the missing-value result is
// pre-multiplied by the active direction to cancel that negation out.
export const makeSortComparator =
  (type: SortValueType): GridComparatorFn =>
  (v1, v2, cellParams1) => {
    const a = toComparable(v1 as Maybe<string | number>, type)
    const b = toComparable(v2 as Maybe<string | number>, type)
    if (a === null && b === null) return 0

    const direction: GridSortDirection = cellParams1.api
      .getSortModel()
      .find((item: { field: string; sort: GridSortDirection }) => item.field === cellParams1.field)?.sort
    const missingLast = direction === 'desc' ? -1 : 1
    if (a === null) return missingLast
    if (b === null) return -missingLast

    if (a < b) return -1
    if (a > b) return 1
    return 0
  }
